import { useEffect, useState } from "react";
import { Eye, Globe, Layers, Plus, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

import {
  canPickAndroidApps,
  canPickScreenTimeApps,
  presentAndroidAppPicker,
  presentAppPicker,
  presentSelectionDisplay,
} from "../../lib/native/rules";
import { normalizeDomainTarget, selectionAppsCategoriesSummary, targetAppKey, validateTargetDraft } from "../../lib/rules/ruleDerivation";
import type { TargetDraft, TargetSelection } from "../../lib/rules/ruleTypes";
import type { WebsiteCategoryId } from "../../lib/rules/websiteCategories";
import { sanitizeDomainCategories } from "../../lib/rules/websiteCategories";
import { useKeyboardInsetWhen } from "../../lib/native/KeyboardInsetProvider";
import { BottomSheet } from "../habits/BottomSheet";
import {
  MAX_VISIBLE_APPS_COMPACT,
  PreviewAppIconsRow,
  selectionHasPickerAppsOrCategories,
  selectionHasPickerWebDomains,
} from "./TargetPreview";
import { iconAppsForTarget } from "../../lib/insights/smartBlockSelection";
import { useWebFilterVpnGate } from "./useWebFilterVpnGate";
import { WebsiteCategoryPickerModal, websiteCategoryLabel } from "./WebsiteCategoryPickerModal";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

export type TargetEditorOrigin = "standalone" | "rule" | "quickStart";

type TargetEditorSheetProps = {
  open: boolean;
  draft: TargetDraft;
  baselineDraft: TargetDraft;
  pending: boolean;
  origin?: TargetEditorOrigin;
  onChange: (draft: TargetDraft) => void;
  onClose: () => void;
  onSave: () => void;
};

export function TargetEditorSheet({
  open,
  draft,
  baselineDraft,
  pending,
  origin = "standalone",
  onChange,
  onClose,
  onSave,
}: TargetEditorSheetProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const vpnGateCopy = t.rules.vpnWebTargetGate;
  const prefersReducedMotion = useReducedMotion();
  const keyboardState = useKeyboardInsetWhen(open);
  const { needsVpn, installVpn, refreshStatus } = useWebFilterVpnGate(open);
  const [domainInput, setDomainInput] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [contentError, setContentError] = useState(false);
  const [vpnWebError, setVpnWebError] = useState(false);
  const [vpnBannerBusy, setVpnBannerBusy] = useState(false);
  const [vpnBannerError, setVpnBannerError] = useState<string | null>(null);
  const [nameShakeKey, setNameShakeKey] = useState(0);
  const [pickerBusy, setPickerBusy] = useState(false);
  const canPickAndroid = canPickAndroidApps();
  const canPick = canPickScreenTimeApps() || canPickAndroid;
  const androidPreviewIconApps = canPickAndroid
    ? iconAppsForTarget({ apps: draft.apps, selection: draft.selection }, "android")
    : [];

  useEffect(() => {
    if (!open) {
      setDomainInput("");
      setCategoryModalOpen(false);
      setNameError(false);
      setContentError(false);
      setVpnWebError(false);
      setVpnBannerBusy(false);
      setVpnBannerError(null);
    }
  }, [open]);

  const domainCategories = sanitizeDomainCategories(draft.domainCategories);
  const isDomainInputEmpty = domainInput.trim() === "";

  const addDomain = () => {
    const domain = normalizeDomainTarget(domainInput);
    if (!domain || draft.domains.includes(domain)) {
      setDomainInput("");
      return;
    }
    onChange({ ...draft, domains: [...draft.domains, domain] });
    setDomainInput("");
    if (contentError) setContentError(false);
    if (vpnWebError) setVpnWebError(false);
  };

  const removeDomain = (domain: string) =>
    onChange({ ...draft, domains: draft.domains.filter((item) => item !== domain) });

  const removeCategory = (id: WebsiteCategoryId) =>
    onChange({ ...draft, domainCategories: domainCategories.filter((item) => item !== id) });

  const handleCategoryChange = (next: WebsiteCategoryId[]) => {
    onChange({ ...draft, domainCategories: next });
    if (next.length > 0 && contentError) setContentError(false);
    if (vpnWebError) setVpnWebError(false);
  };

  const handleVpnBannerInstall = async () => {
    if (vpnBannerBusy) return;
    setVpnBannerBusy(true);
    setVpnBannerError(null);
    try {
      const result = await installVpn();
      if (!result.installed) {
        setVpnBannerError(result.reason ?? result.status);
        return;
      }
      await refreshStatus();
    } catch (caught) {
      setVpnBannerError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setVpnBannerBusy(false);
    }
  };

  const pickApps = async () => {
    if (pickerBusy) return;
    setPickerBusy(true);
    try {
      const selectionId = draft.selection?.id ?? crypto.randomUUID();

      if (canPickAndroid) {
        // Android returns real package names — store them as typed apps (so the card
        // shows real icons) and keep a selection for native enforcement resolution.
        const result = await presentAndroidAppPicker(selectionId);
        if (result.canceled) return;
        const apps = result.apps.map((app) => ({
          key: targetAppKey({ appIdentifier: app.packageName, displayName: app.displayName }),
          label: app.displayName,
          appIdentifier: app.packageName,
        }));
        onChange({
          ...draft,
          apps,
          selection:
            result.applicationCount > 0
              ? {
                  id: selectionId,
                  applicationCount: result.applicationCount,
                  categoryCount: 0,
                  webDomainCount: 0,
                  updatedAt: Date.now(),
                }
              : undefined,
        });
        if (result.applicationCount > 0 && contentError) setContentError(false);
        return;
      }

      const result = await presentAppPicker(selectionId);
      if (result.canceled) return;
      const total = result.applicationCount + result.categoryCount + result.webDomainCount;
      onChange({
        ...draft,
        selection:
          total > 0
            ? {
                id: selectionId,
                applicationCount: result.applicationCount,
                categoryCount: result.categoryCount,
                webDomainCount: result.webDomainCount,
                updatedAt: Date.now(),
              }
            : undefined,
      });
      if (total > 0 && contentError) setContentError(false);
    } finally {
      setPickerBusy(false);
    }
  };

  const handleSave = () => {
    const validation = validateTargetDraft(draft, {
      baseline: baselineDraft,
      needsVpnForTypedWeb: needsVpn,
    });
    let blocked = false;

    if (validation.nameRequired) {
      setNameError(true);
      setNameShakeKey((key) => key + 1);
      blocked = true;
    } else {
      setNameError(false);
    }

    if (validation.contentRequired) {
      setContentError(true);
      blocked = true;
    } else {
      setContentError(false);
    }

    if (validation.vpnRequiredForWebAdditions) {
      setVpnWebError(true);
      blocked = true;
    } else {
      setVpnWebError(false);
    }

    if (blocked) return;
    onSave();
  };

  const contextLabel =
    origin === "rule"
      ? t.rules.targetEditorFromRule
      : origin === "quickStart"
        ? t.rules.targetEditorFromQuickStart
        : null;
  const dismissLabel =
    origin === "rule" ? t.rules.backToRule : origin === "quickStart" ? t.rules.backToQuickStart : t.common.cancel;

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="target-editor-title">
      <div className="flex min-h-0 flex-col">
        <div
          className={`flex shrink-0 items-center justify-between px-4 pb-1 ${keyboardState.bottomInset > 0 ? "pt-[max(16px,env(safe-area-inset-top))]" : "pt-4"}`}
        >
          <div className="min-w-0 flex-1 pr-2">
            {contextLabel ? (
              <p className="pb-0.5 text-xs font-medium text-[oklch(72%_0.09_155)]">{contextLabel}</p>
            ) : null}
            <h2 id="target-editor-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
              {draft._id ? t.rules.editTarget : t.rules.newTargetEditor}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="icon-button" style={{ minWidth: 44, minHeight: 44, border: "none" }} aria-label={t.common.close}>
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 max-h-[64svh] overflow-y-auto px-4 pb-2">
        <label className="grid gap-1.5 pb-3 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
          {t.rules.targetNameLabel}
          <motion.div
            key={nameShakeKey}
            animate={
              nameError && !prefersReducedMotion
                ? { x: [0, -8, 8, -5, 5, 0] }
                : { x: 0 }
            }
            transition={{ duration: 0.38, ease: "easeOut" }}
          >
            <input
              value={draft.name}
              onChange={(event) => {
                onChange({ ...draft, name: event.target.value });
                if (nameError && event.target.value.trim()) setNameError(false);
              }}
              placeholder={t.rules.targetNamePlaceholder}
              aria-invalid={nameError}
              className={`liquid-glass-input min-h-11 w-full px-3 text-base font-normal normal-case tracking-normal text-[oklch(90%_0.02_150)] transition-[box-shadow] ${
                nameError ? "liquid-glass-input-error" : ""
              }`}
            />
          </motion.div>
          {nameError ? (
            <span className="text-xs font-normal normal-case tracking-normal text-[oklch(72%_0.12_25)]">
              {t.rules.targetNameRequired}
            </span>
          ) : null}
        </label>

        <section className="pb-3">
          <h3 className="pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
            {t.rules.targetAppsCategoriesSection}
          </h3>
          {draft.selection ? (
            <>
              <SelectionRow
                selection={draft.selection}
                canChange={canPick}
                canViewNative={!canPickAndroid}
                busy={pickerBusy}
                onChange={() => void pickApps()}
                t={t}
              />
              {androidPreviewIconApps.length > 0 ? (
                <PreviewAppIconsRow
                  apps={androidPreviewIconApps}
                  maxVisible={MAX_VISIBLE_APPS_COMPACT}
                  size="compact"
                />
              ) : null}
            </>
          ) : canPick ? (
            <button
              type="button"
              onClick={() => void pickApps()}
              disabled={pickerBusy}
              className="secondary-action w-full justify-center disabled:opacity-60"
            >
              <ShieldCheck size={15} aria-hidden />
              {pickerBusy ? t.common.opening : t.rules.chooseAppsSites}
            </button>
          ) : (
            <p className="liquid-glass-inset p-2.5 text-xs leading-5 text-[oklch(64%_0.025_155)]">
              {t.rules.targetOpenOnIphone}
            </p>
          )}
          {contentError ? (
            <p className="mt-2 text-xs text-[oklch(72%_0.12_25)]">{t.rules.targetContentRequired}</p>
          ) : null}
        </section>

        <section>
          <h3 className="pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">{t.rules.targetWebsitesSection}</h3>
          {needsVpn ? (
            <div className="grid gap-2">
              <button
                type="button"
                onClick={() => void handleVpnBannerInstall()}
                disabled={vpnBannerBusy}
                className="liquid-glass-dashed-cta flex min-h-11 w-full items-center gap-3 px-3 py-4 text-left transition-transform active:scale-[0.99] disabled:opacity-60"
                aria-label={vpnGateCopy.install}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]">
                  <Globe size={18} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm leading-5 text-[oklch(82%_0.025_155)]">{vpnGateCopy.banner}</span>
                  <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-[oklch(72%_0.09_155)]">
                    {vpnBannerBusy ? vpnGateCopy.installing : vpnGateCopy.install}
                  </span>
                </span>
              </button>
              {vpnBannerError ? (
                <div className="liquid-glass-alert-warning flex gap-2 p-3 text-sm text-[oklch(82%_0.055_85)]">
                  <ShieldAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
                  <p className="break-words font-mono text-xs leading-5">{vpnBannerError}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <input
                  value={domainInput}
                  onChange={(event) => setDomainInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addDomain();
                    }
                  }}
                  placeholder={t.rules.websitePlaceholder}
                  inputMode="url"
                  className="liquid-glass-input min-h-11 min-w-0 px-3 text-base text-[oklch(90%_0.02_150)]"
                  aria-label={t.rules.websiteAria}
                />
                <button
                  type="button"
                  onClick={addDomain}
                  disabled={isDomainInputEmpty}
                  className="secondary-action min-w-[5.5rem] shrink-0 px-3 disabled:opacity-50"
                  aria-label={t.websiteCategories.addWebsiteAria}
                >
                  <Plus size={15} aria-hidden />
                  {t.common.add}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setCategoryModalOpen(true)}
                className="mt-2 flex min-h-11 w-full items-center gap-2 rounded-lg px-1 text-left text-sm font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(20%_0.02_165)]"
                aria-label={t.websiteCategories.browseCategoriesAria}
              >
                <Layers size={15} className="shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="block">{t.rules.websiteCategoriesLink}</span>
                  <span className="block text-xs font-normal text-[oklch(64%_0.025_155)]">{t.rules.websiteCategoriesHint}</span>
                </span>
              </button>
              {domainCategories.length > 0 || draft.domains.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {domainCategories.map((categoryId) => (
                    <button
                      key={categoryId}
                      type="button"
                      onClick={() => removeCategory(categoryId)}
                      className="liquid-glass-badge items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[oklch(82%_0.06_155)]"
                      aria-label={formatTemplate(t.websiteCategories.removeCategoryAria, {
                        name: websiteCategoryLabel(categoryId, t),
                      })}
                    >
                      <Layers size={12} aria-hidden />
                      {websiteCategoryLabel(categoryId, t)}
                      <X size={12} aria-hidden />
                    </button>
                  ))}
                  {draft.domains.map((domain) => (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => removeDomain(domain)}
                      className="liquid-glass-chip items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[oklch(82%_0.025_155)]"
                    >
                      <Globe size={12} aria-hidden />
                      {domain}
                      <X size={12} aria-hidden />
                    </button>
                  ))}
                </div>
              ) : null}
              {vpnWebError ? (
                <p className="mt-2 text-xs text-[oklch(72%_0.12_25)]">{vpnGateCopy.saveBlocked}</p>
              ) : null}
              {draft.selection && selectionHasPickerWebDomains(draft.selection) ? (
                <p className="mt-2 text-xs text-[oklch(64%_0.025_155)]">
                  {formatTemplate(t.rules.targetSitesFromPicker, {
                    count: draft.selection.webDomainCount,
                    siteWord: draft.selection.webDomainCount === 1 ? t.rules.siteSingular : t.rules.sitePlural,
                  })}
                </p>
              ) : null}
            </>
          )}
        </section>
        </div>

        <div className="liquid-glass-sheet-footer flex shrink-0 gap-2 px-4 pt-3">
          <button type="button" onClick={onClose} className="secondary-action flex-1">{dismissLabel}</button>
          <button type="button" onClick={handleSave} className="primary-action flex-1" disabled={pending}>
            {draft._id ? t.rules.saveTarget : t.rules.createTarget}
          </button>
        </div>
      </div>

      <WebsiteCategoryPickerModal
        open={categoryModalOpen}
        selected={domainCategories}
        onChange={handleCategoryChange}
        onClose={() => {
          setCategoryModalOpen(false);
          void refreshStatus();
        }}
        canAddCategories={!needsVpn}
      />
    </BottomSheet>
  );
}

function SelectionRow({
  selection,
  canChange,
  canViewNative,
  busy,
  onChange,
  t,
}: {
  selection: TargetSelection;
  canChange: boolean;
  canViewNative: boolean;
  busy: boolean;
  onChange: () => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const showView =
    canChange &&
    canViewNative &&
    (selectionHasPickerAppsOrCategories(selection) || selectionHasPickerWebDomains(selection));

  const body = (
    <>
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[oklch(20%_0.025_160)] text-[oklch(72%_0.09_155)]">
        <ShieldCheck size={16} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-[oklch(88%_0.02_150)]">{selectionAppsCategoriesSummary(selection)}</span>
        <span className="block truncate text-xs text-[oklch(64%_0.025_155)]">
          {canChange ? t.rules.tapToChangeBlocked : t.rules.chosenOnDevice}
        </span>
      </span>
      {canChange ? (
        <span className="liquid-glass-icon-ring grid h-7 w-7 shrink-0 place-items-center text-[oklch(72%_0.09_155)]">
          <Plus size={14} aria-hidden />
        </span>
      ) : null}
    </>
  );

  return (
    <div className="grid gap-2">
      {canChange ? (
        <button
          type="button"
          onClick={onChange}
          disabled={busy}
          aria-label={t.rules.changeBlockedAppsAria}
          className="liquid-glass-inset flex w-full items-center gap-3 p-2.5 text-left transition-transform active:scale-[0.99] disabled:opacity-60"
        >
          {body}
        </button>
      ) : (
        <div className="liquid-glass-inset flex w-full items-center gap-3 p-2.5 text-left">{body}</div>
      )}

      {showView ? (
        <div className="flex justify-end px-0.5">
          <button
            type="button"
            onClick={() => void presentSelectionDisplay(selection.id)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(20%_0.02_165)]"
          >
            <Eye size={13} aria-hidden />
            {t.rules.view}
          </button>
        </div>
      ) : null}
    </div>
  );
}
